// v1.20.2 — InputBar isolé en composant mémorisé.
// Le state du textarea vit LOCALEMENT ici, donc taper du texte ne déclenche
// plus de re-render du Chat parent (qui contient les bulles + Markdown).
//
// v2.16.2a — Click-outside + Escape sur les 2 dropdowns (Mode + Profil LLM).
//          — Label dynamique du bouton Mode selon themeMode + accent color.

import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
// v2.19.21a — Icônes lucide style emergent.sh pour les 2 boutons d'outils.
// v2.19.21c-ui — SlidersHorizontal (3 traits) remplacé par SlidersTwoIcon inline
// (exactement 2 traits avec un curseur sur chacun, style emergent.sh).
import { Paperclip } from "lucide-react";

const SlidersTwoIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <line x1="4" y1="8" x2="20" y2="8" />
    <circle cx="14" cy="8" r="2.5" fill="currentColor" />
    <line x1="4" y1="16" x2="20" y2="16" />
    <circle cx="9" cy="16" r="2.5" fill="currentColor" />
  </svg>
);

// Mapping themeMode → label/accent pour le bouton trigger.
const MODE_TRIGGER_META = {
  food:           { label: "🍽️ Food",           accent: "#F97316" },
  beaute:         { label: "💅 Bien-être & Beauté", accent: "#EC4899" },
  quotidien:      { label: "🌆 Quotidien",      accent: "#FF7E1B" },
  justice:        { label: "⚖️ Justice",        accent: "#1E40AF" },
  sante:          { label: "🏥 Santé",          accent: "#DC2626" },
  divertissement: { label: "🌴 Détente",        accent: "#A855F7" },
  emploi:         { label: "💼 Emploi",         accent: "#10B981" },
  administration: { label: "🛂 Démarches",      accent: "#3B82F6" },
  finance:        { label: "💰 Finance",        accent: "#059669" },
  shopping:       { label: "🛍 Shopping",       accent: "#EC4899" },
  // [SPRINT K P1] Mode SERVICES — navigation directe vers la page prestataires.
  services:       { label: "🛠️ Services",       accent: "#D4AF37" },
};

// v2.17.0 — Style "enrobé" unifié pour les items du dropdown (utilisé quand actif).
function enrobedStyle(accent) {
  return {
    background: `${accent}26`,
    borderLeft: `4px solid ${accent}`,
    color: accent,
    fontWeight: 700,
  };
}

const InputBarInner = forwardRef(function InputBarInner(
  {
    loading,
    onSend,
    mode,
    setMode,
    voiceOutputEnabled,
    setVoiceOutputEnabled,
    theme,
    setTheme,
    rapideMode,
    setRapideMode,
    themeMode,
    setThemeMode,
    minimal = false,
    smallVoiceButton,
    // v2.19.21e — Trombone activée : upload doc/image vers /api/upload-doc.
    attachedDoc,        // { doc_id, name, kind, preview, size } | null
    onPickFile,         // (File) => void
    onRemoveAttachedDoc,// () => void
    docUploading,
    // v2.19.24 — Mode Ordonnance médicale (caméra arrière).
    onPickOrdonnance,
  },
  ref,
) {
  const [input, setInput] = useState("");
  const taRef = useRef(null);
  const fileInputRef = useRef(null); // v2.19.21e
  const ordoInputRef = useRef(null); // v2.19.24
  // v2.16.2a — refs sur les 2 <details> pour pouvoir les fermer programmatiquement.
  const modeDetailsRef = useRef(null);
  const profileDetailsRef = useRef(null);
  // [SPRINT K P2 BUG-K1 FIX 2026-07-01] Dropdown MODES converti en composant contrôlé + React Portal
  // pour échapper au overflow:hidden + transform stacking-context de <LiveBanner>.
  const modeTriggerRef = useRef(null);
  const modePortalRef = useRef(null);
  const [modeOpen, setModeOpen] = useState(false);
  const [modeMenuPos, setModeMenuPos] = useState({ bottom: 60, left: 20 });

  useImperativeHandle(ref, () => ({
    setValue: (v) => { setInput(v || ""); taRef.current?.focus(); },
    clear: () => setInput(""),
    focus: () => taRef.current?.focus(),
    getValue: () => input,
  }));

  const submit = useCallback(() => {
    const text = (input || "").trim();
    if (!text || loading) return;
    setInput("");
    onSend(text);
  }, [input, loading, onSend]);

  const onKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  }, [submit]);

  const onChange = useCallback((e) => setInput(e.target.value), []);

  // v3.0.1 — FIX pad : auto-resize du textarea (1 → ~6 lignes puis scroll interne).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const max = 160;
    ta.style.height = Math.min(ta.scrollHeight, max) + "px";
    ta.style.overflowY = ta.scrollHeight > max ? "auto" : "hidden";
  }, [input]);

  // v2.16.2a — Click-outside (pointerdown = mouse + touch + pen) + Escape.
  // Ferme tout <details> ouvert dont la cible du click n'est pas un descendant.
  // [SPRINT K P2 BUG-K1] Gère aussi le dropdown MODES contrôlé (portal) : ferme si click hors trigger + hors portal.
  useEffect(() => {
    const closeIfOutside = (el, target) => {
      if (el && el.open && !el.contains(target)) el.open = false;
    };
    const onPointerDown = (e) => {
      // <details> LLM profile (natif) : legacy comportement.
      closeIfOutside(profileDetailsRef.current, e.target);
      // Dropdown MODES contrôlé : ferme si click hors trigger ET hors portal.
      if (modeOpen) {
        const trig = modeTriggerRef.current;
        const port = modePortalRef.current;
        const inTrigger = trig && trig.contains(e.target);
        const inPortal = port && port.contains(e.target);
        if (!inTrigger && !inPortal) setModeOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (profileDetailsRef.current?.open) profileDetailsRef.current.open = false;
      if (modeOpen) setModeOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [modeOpen]);

  // [SPRINT K P2 BUG-K1] Recalcule la position du menu portal sur scroll/resize/open.
  useEffect(() => {
    if (!modeOpen) return;
    const recompute = () => {
      const trig = modeTriggerRef.current;
      if (!trig) return;
      const rect = trig.getBoundingClientRect();
      // Ouvre TOUJOURS vers le haut (InputBar en bas de l'écran) via `bottom` — pas besoin de mesurer la hauteur du menu.
      setModeMenuPos({
        bottom: Math.max(8, window.innerHeight - rect.top + 6),
        left: Math.max(8, rect.left),
      });
    };
    recompute();
    window.addEventListener("scroll", recompute, { passive: true, capture: true });
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, { capture: true });
      window.removeEventListener("resize", recompute);
    };
  }, [modeOpen]);

  // Helper : sélectionne un mode puis ferme le dropdown.
  const navigate = useNavigate();
  const selectThemeMode = (v) => {
    if (setThemeMode) setThemeMode(v);
    // [SPRINT K P2 BUG-K1] Ferme le dropdown MODES contrôlé (Portal).
    setModeOpen(false);
    // [SPRINT K P2 BUG-K2 FIX 2026-07-01] navigate('/services') retiré.
    // Le mode "services" ne navigue plus : il déclenche uniquement le chip swap dans Chat.jsx::useEffect (L289).
  };
  // [SPRINT K P2 BUG-K1] Toggle trigger du dropdown MODES (Portal).
  const toggleModeOpen = () => {
    setModeOpen((prev) => !prev);
  };
  // Helper : sélectionne un profil LLM puis ferme le dropdown.
  const selectLlmProfile = (k) => {
    if (setMode) setMode(k);
    if (profileDetailsRef.current) profileDetailsRef.current.open = false;
  };

  const modeMeta = MODE_TRIGGER_META[themeMode];
  // v3.0.0-rc.8s — Tronque le label à 8 caractères (après emoji) pour éviter
  // que le pad se déforme avec "Divertissement", "Démarches" etc.
  const _truncateModeLabel = (full) => {
    if (!full) return "🎭 Mode";
    // Sépare emoji (1-2 chars) du texte
    const m = full.match(/^(\S+)\s+(.+)$/);
    if (!m) return full;
    const [, emoji, text] = m;
    const MAX = 8;
    return text.length > MAX ? `${emoji} ${text.slice(0, MAX)}…` : full;
  };
  const modeButtonLabel = _truncateModeLabel(modeMeta?.label);
  const modeButtonAccent = modeMeta?.accent;
  const modeTriggerStyle = modeButtonAccent
    ? { color: modeButtonAccent, borderColor: `${modeButtonAccent}66`, fontWeight: 700 }
    : undefined;

  return (
    <div className="civ-input-shell" data-testid="input-shell">
      {/* v2.19.21e — Chip d'attachement (PDF/TXT/MD/image joint au prochain message). */}
      {attachedDoc && (
        <div className="civ-attached-doc" data-testid="attached-doc-chip">
          <span className="civ-attached-doc-icon" aria-hidden="true">
            {attachedDoc.kind === "image" ? "🖼" : "📄"}
          </span>
          <span className="civ-attached-doc-name" title={attachedDoc.name}>
            {attachedDoc.name}
          </span>
          <span className="civ-attached-doc-meta" title={attachedDoc.preview}>
            {attachedDoc.kind === "image"
              ? `${Math.round((attachedDoc.size || 0) / 1024)} Ko`
              : (attachedDoc.preview || "").slice(0, 60) + ((attachedDoc.preview || "").length > 60 ? "…" : "")}
          </span>
          <button
            type="button"
            className="civ-attached-doc-remove"
            data-testid="attached-doc-remove"
            aria-label="Retirer le document joint"
            title="Retirer"
            onClick={() => { if (typeof onRemoveAttachedDoc === "function") onRemoveAttachedDoc(); }}
          >×</button>
        </div>
      )}
      {docUploading && (
        <div className="civ-attached-doc-uploading" data-testid="attached-doc-uploading">
          📎 Upload en cours…
        </div>
      )}
      <textarea
        ref={taRef}
        className="civ-textarea"
        value={input}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="Demandez à AYA..."
        rows={1}
        data-testid="message-input"
      />
      <div className="civ-input-toolbar" data-testid="input-toolbar">
        <div className="civ-input-toolbar-left">
          <button
            type="button"
            className="civ-tool-btn civ-tool-btn-circle civ-tool-btn-icon"
            data-testid="input-attach-paperclip"
            title={attachedDoc ? `Joint : ${attachedDoc.name}` : "Joindre un fichier (PDF, TXT, MD, image — max 5 Mo)"}
            aria-label="Joindre un fichier"
            onClick={() => fileInputRef.current?.click()}
            disabled={docUploading}
          >
            <Paperclip size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,image/png,image/jpeg,image/webp"
            hidden
            data-testid="input-attach-file-hidden"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f && typeof onPickFile === "function") onPickFile(f);
              // Reset input pour permettre de re-sélectionner le MÊME fichier après suppression.
              e.target.value = "";
            }}
          />
          {/* v2.19.24 → v2.19.27 : Bouton ✨ MAGIC (analyse multi-document via classifier Vision). */}
          <button
            type="button"
            className="civ-tool-btn civ-tool-btn-circle civ-tool-btn-icon"
            data-testid="input-magic-btn"
            title="Analyser un document"
            aria-label="Analyser un document"
            onClick={() => ordoInputRef.current?.click()}
            disabled={docUploading}
            style={{ marginLeft: 4 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
              <path d="M5 17l.75 2 .75-2 2-.75-2-.75-.75-2-.75 2-2 .75 2 .75z" />
              <path d="M19 15l.5 1.5L21 17l-1.5.5L19 19l-.5-1.5L17 17l1.5-.5L19 15z" />
            </svg>
          </button>
          <input
            ref={ordoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            data-testid="input-magic-file-hidden"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f && typeof onPickOrdonnance === "function") onPickOrdonnance(f);
              e.target.value = "";
            }}
          />
          <div
            ref={modeTriggerRef}
            className="civ-tool-dropdown"
            data-testid="input-tools-dropdown"
            style={{ display: minimal ? "none" : "inline-block", position: "relative" }}
          >
            <button
              type="button"
              className="civ-tool-btn"
              data-testid="mode-button-trigger"
              style={modeTriggerStyle}
              onClick={toggleModeOpen}
              aria-expanded={modeOpen}
              aria-haspopup="menu"
            >
              <span style={{
                maxWidth: 96,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
                verticalAlign: "middle",
              }}>{modeButtonLabel}</span>
              <span className="civ-tool-caret">▾</span>
            </button>
          </div>
          {modeOpen && ReactDOM.createPortal(
            <div
              ref={modePortalRef}
              className="aya-mode-dropdown-portal civ-tool-menu"
              role="menu"
              data-testid="mode-dropdown-portal"
              style={{
                position: "fixed",
                bottom: `${modeMenuPos.bottom}px`,
                left: `${modeMenuPos.left}px`,
                zIndex: 99999,
                maxHeight: "40vh",
                overflowY: "auto",
                overscrollBehavior: "contain",
                isolation: "isolate",
              }}
            >
              <div className="civ-tool-menu-section">Thème</div>
              {/* v3.21.0 — Food / Shopping / Services en tête + séparateur orange. */}
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "food" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("food")}
                role="menuitemradio"
                aria-checked={themeMode === "food"}
                data-testid="mode-food-btn"
                style={themeMode === "food" ? enrobedStyle("#F97316") : undefined}
              >
                🍽️ Food {themeMode === "food" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "shopping" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("shopping")}
                role="menuitemradio"
                aria-checked={themeMode === "shopping"}
                data-testid="mode-shopping-btn"
                style={themeMode === "shopping" ? enrobedStyle("#EC4899") : undefined}
              >
                🛍 Shopping {themeMode === "shopping" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "services" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("services")}
                role="menuitemradio"
                aria-checked={themeMode === "services"}
                data-testid="mode-services-btn"
                style={themeMode === "services" ? enrobedStyle("#D4AF37") : undefined}
              >
                🛠️ Services {themeMode === "services" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "beaute" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("beaute")}
                role="menuitemradio"
                aria-checked={themeMode === "beaute"}
                data-testid="mode-beaute-btn"
                style={themeMode === "beaute" ? enrobedStyle("#EC4899") : undefined}
              >
                💅 Bien-être & Beauté {themeMode === "beaute" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "divertissement" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("divertissement")}
                role="menuitemradio"
                aria-checked={themeMode === "divertissement"}
                data-testid="mode-divertissement-btn"
                style={themeMode === "divertissement" ? enrobedStyle("#A855F7") : undefined}
              >
                🌴 Détente {themeMode === "divertissement" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <div
                data-testid="mode-menu-separator"
                aria-hidden="true"
                style={{ height: 2, margin: "6px 10px", borderRadius: 2, background: "linear-gradient(90deg, #F97316, rgba(249,115,22,0.15))" }}
              />
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "quotidien" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("quotidien")}
                role="menuitemradio"
                aria-checked={themeMode === "quotidien"}
                data-testid="mode-quotidien-btn"
                style={themeMode === "quotidien" ? enrobedStyle("#FF7E1B") : undefined}
              >
                🌆 Quotidien {themeMode === "quotidien" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "justice" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("justice")}
                role="menuitemradio"
                aria-checked={themeMode === "justice"}
                data-testid="mode-justice-btn"
                style={themeMode === "justice" ? enrobedStyle("#1E40AF") : undefined}
              >
                ⚖️ Justice {themeMode === "justice" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "sante" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("sante")}
                role="menuitemradio"
                aria-checked={themeMode === "sante"}
                data-testid="mode-sante-btn"
                style={themeMode === "sante" ? enrobedStyle("#DC2626") : undefined}
              >
                🏥 Santé {themeMode === "sante" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "emploi" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("emploi")}
                role="menuitemradio"
                aria-checked={themeMode === "emploi"}
                data-testid="mode-emploi-btn"
                style={themeMode === "emploi" ? enrobedStyle("#10B981") : undefined}
              >
                💼 Emploi {themeMode === "emploi" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "administration" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("administration")}
                role="menuitemradio"
                aria-checked={themeMode === "administration"}
                data-testid="mode-administration-btn"
                style={themeMode === "administration" ? enrobedStyle("#3B82F6") : undefined}
              >
                🛂 Démarches {themeMode === "administration" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              <button
                type="button"
                className={`civ-tool-menu-item ${themeMode === "finance" ? "is-active" : ""}`}
                onClick={() => selectThemeMode("finance")}
                role="menuitemradio"
                aria-checked={themeMode === "finance"}
                data-testid="mode-finance-btn"
                style={themeMode === "finance" ? enrobedStyle("#059669") : undefined}
              >
                💰 Finance {themeMode === "finance" && <span style={{ opacity: 0.7 }}> •</span>}
              </button>
              {/* v2.17.0a — Sections "Sortie vocale" + "Apparence" retirées.
                  Les toggles correspondants sont déplacés dans le header de l'app
                  (data-testid="header-theme-toggle-btn" + "header-voice-toggle-btn"). */}
            </div>,
            document.body,
          )}
        </div>
        <div className="civ-input-toolbar-right">
          <details ref={profileDetailsRef} className="civ-tool-dropdown civ-rapide-dropdown" data-testid="input-rapide-dropdown">
            <summary
              className="civ-tool-btn civ-tool-btn-icon"
              data-testid="input-settings-llm-style"
              title="Style de réponse LLM"
              aria-label="Style de réponse LLM"
            >
              <SlidersTwoIcon strokeWidth={1.75} aria-hidden="true" />
              <span className="civ-tool-btn-sublabel">
                {mode === "casual" && "Casual"}
                {mode === "street" && "Nouchi"}
                {mode === "equilibre" && "Équilibré"}
                {mode === "precis" && "Précis"}
                {mode === "creatif" && "Créatif"}
                {mode === "business" && "Business"}
                {!["casual","street","equilibre","precis","creatif","business"].includes(mode) && "Casual"}
              </span>
            </summary>
            <div className="civ-tool-menu" role="menu" style={{ maxHeight: "40vh", overflowY: "auto", overscrollBehavior: "contain" }}>
              <div className="civ-tool-menu-section">Profil LLM</div>
              {[
                { k: "casual", l: "🟢 Casual" },
                { k: "street", l: "🔴 Nouchi (street)" },
                { k: "equilibre", l: "🔵 Équilibré" },
                { k: "precis", l: "🎯 Précis" },
                { k: "creatif", l: "🟣 Créatif" },
              ].map((p) => (
                <button
                  key={p.k}
                  type="button"
                  className={`civ-tool-menu-item ${mode === p.k ? "is-active" : ""}`}
                  onClick={() => selectLlmProfile(p.k)}
                  role="menuitemradio"
                  aria-checked={mode === p.k}
                  data-testid={`input-llm-profile-${p.k}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </details>
          {smallVoiceButton}
          <button
            className="civ-send-btn-round"
            onClick={submit}
            disabled={loading || !input.trim()}
            data-testid="send-button"
            type="button"
            aria-label="Envoyer"
          >
            {loading ? (
              <span className="civ-send-spinner" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="19" x2="12" y2="5"></line>
                <polyline points="5 12 12 5 19 12"></polyline>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

const InputBar = memo(InputBarInner, (prev, next) => {
  return (
    prev.loading === next.loading &&
    prev.mode === next.mode &&
    prev.voiceOutputEnabled === next.voiceOutputEnabled &&
    prev.theme === next.theme &&
    prev.rapideMode === next.rapideMode &&
    prev.smallVoiceButton === next.smallVoiceButton &&
    prev.onSend === next.onSend &&
    prev.setMode === next.setMode &&
    prev.setVoiceOutputEnabled === next.setVoiceOutputEnabled &&
    prev.setTheme === next.setTheme &&
    prev.setRapideMode === next.setRapideMode &&
    prev.themeMode === next.themeMode &&
    prev.setThemeMode === next.setThemeMode &&
    // v2.19.21e
    prev.attachedDoc === next.attachedDoc &&
    prev.onPickFile === next.onPickFile &&
    prev.onRemoveAttachedDoc === next.onRemoveAttachedDoc &&
    prev.docUploading === next.docUploading
  );
});

export default InputBar;
